import { Elements, PaymentMethodMessagingElement } from "@stripe/react-stripe-js";

import { useTranslation } from "@/core/i18n";
import { BeautifiRateLine } from "atoms/beautifi/beautifiRateLine/BeautifiRateLine";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { currencyService } from "toolbox/CurrencyService";
import { formatCurrency } from "toolbox/Money";

type PricingPreviewProps = {
  defaultPrice: number;
  memberPrice?: number;
  isPriceSet: boolean;
  packageUnitType: string | null | undefined;
  affirmEnabled?: boolean;
  beautifiEnabled?: boolean;
  type?: "Treatment" | "Package" | "Product" | "ProductFromExternalSource";
  isMember?: boolean;
};

export const PricingPreview = ({
  defaultPrice,
  memberPrice,
  isPriceSet,
  packageUnitType,
  affirmEnabled = false,
  beautifiEnabled = false,
  type = "Treatment",
  isMember = false,
}: PricingPreviewProps) => {
  const { t } = useTranslation();
  const stripeKey = useStripePublishableKey();

  const stripePromise = stripeLoader.getStripe(stripeKey);
  const currency = currencyService.getCurrency();

  const defaultPricing = (
    <div className="text-body1">
      {isPriceSet || type == "Package" ? "" : t("pricingPreview.fromPrefix")}
      <span className="text-bold1">{formatCurrency(defaultPrice)}</span>
      {!isPriceSet || type == "Package" ? "" : `/${packageUnitType}`}
    </div>
  );

  const memberPricing = (
    <div className="text-body1">
      <span>{isPriceSet || type == "Package" ? "" : t("pricingPreview.fromPrefix")}</span>
      <span className="pr-[6px]">
        {formatCurrency(defaultPrice)}
        {!isPriceSet || type == "Package" ? "" : `/${packageUnitType}`}
      </span>
      <span className="border-l border-black pl-[6px] text-bold1 text-primary">
        {memberPrice ? formatCurrency(memberPrice) : ""} {t("pricingPreview.memberSuffix")}
      </span>
    </div>
  );

  const stripeElementAppearance = {
    variables: {
      fontSizeBase: "15px",
      colorText: "#6B7070",
    },
    rules: {
      ".PaymentMethodMessaging": {
        textAlign: "center",
      },
    },
  };

  const effectivePriceInCents = isMember ? (memberPrice ?? defaultPrice) : defaultPrice;

  return (
    <div data-testid="pricing-preview">
      <div className="flex flex-col items-center pb-2">
        {memberPrice ? memberPricing : defaultPricing}
      </div>
      {affirmEnabled && (
        <Elements stripe={stripePromise} options={{ appearance: stripeElementAppearance }}>
          <PaymentMethodMessagingElement
            options={{
              amount: effectivePriceInCents,
              currency: currency.code as "USD" | "CAD",
              paymentMethodTypes: ["affirm"],
              countryCode: currency.code == "CAD" ? "CA" : "US",
            }}
          />
        </Elements>
      )}
      {beautifiEnabled && <BeautifiRateLine priceInCents={effectivePriceInCents} />}
    </div>
  );
};
